import React, { useReducer, useState, useCallback } from "react";
import type { ReactElement } from "react";

import { removeAuthInfoFromLocalStorage } from "pianofunclub-crm/utils/auth";
import { getValueFromLocalStorage } from "pianofunclub-crm/utils/localStorage";

import { AuthContext } from "pianofunclub-shared/providers/AuthProviderContext";
import type {
  AuthAction,
  AuthContextType,
  AuthState,
} from "pianofunclub-shared/providers/AuthProviderContext";
import type { LogoutMutation$data } from "pianofunclub-shared/relay/graphql/auth/__generated__/LogoutMutation.graphql";
import type { MeQuery$data } from "pianofunclub-shared/relay/graphql/general/__generated__/MeQuery.graphql";

interface Props {
  children?: ReactElement;
}

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<MeQuery$data["me"] | null>(null);

  const [authState, dispatchAuthState] = useReducer(
    (prevState: AuthState, action: AuthAction): AuthState => {
      switch (action.type) {
        case "ADD_TOKEN":
          return {
            ...prevState,
            accountType: action.accountType ?? null,
            userToken: action.token,
          };
        case "REMOVE_TOKEN":
          return {
            ...prevState,
            userToken: null,
          };
      }
    },
    getValueFromLocalStorage("token")
      ? {
          accountType: getValueFromLocalStorage("account_type") ?? null,
          userToken: getValueFromLocalStorage("token"),
        }
      : {
          accountType: null,
          userToken: null,
        }
  );

  const clientSideLogout = useCallback(() => {
    dispatchAuthState({ type: "REMOVE_TOKEN" });
    setUser(null);
    removeAuthInfoFromLocalStorage();
  }, [dispatchAuthState, setUser]);

  const logout = useCallback<AuthContextType["logout"]>(
    (variables) => {
      const { commitLogout, onCompleted, resetRelayEnvironment } = variables;

      clientSideLogout();

      // now call logout on django back-end
      commitLogout({
        variables: {
          input: {},
        },
        onCompleted: (response: LogoutMutation$data) => {
          if (response?.logout?.success) {
            resetRelayEnvironment();
            onCompleted?.();
          }
        },
      });
    },
    [clientSideLogout]
  );

  return (
    <AuthContext.Provider
      value={{
        authState,
        clientSideLogout,
        dispatchAuthState,
        logout,
        setUser,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
